<template>
  <BaseModal ref="modal" @after-enter="afterEnter">
    <UiModalDialog
      class="jps-modal-input"
      :no-closer="!isClosable"
      :dialog-width="_dialogWidth"
      :no-header="!hasHeader && !isClosable"
      :dialog-min-height="!hasHeader && !isClosable ? '9rem' : '12rem'"
      dialog-min-width="320px"
    >
      <template #header>
        <BaseIcon size="size-6" v-if="_icon" :name="_icon" />
        {{ _titleVars ? lst(_title, _titleVars) : ls(_title) }}
      </template>
      <div class="jps-modal-content">
        <p v-if="_message">{{ _messageVars ? lst(_message, _messageVars) : ls(_message) }}</p>
        <form @submit.prevent="confirm()">
          <textarea
            v-if="_area"
            class="jps-textarea"
            ref="textareaEl"
            v-model="text"
            rows="4"
          />
          <input v-else type="text" class="jps-input" ref="textEl" v-model="text" />
        </form>
      </div>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="confirm()">{{ ls(dapLangGlobal.ok) }}</button>
      </template>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal } from '#components'
import type { SVG_NAMES } from '~/components/icons'
import type { ModalProps } from '~/components/base/BaseModal.vue'

const { ls, lst } = useI18n()

const modal = ref<InstanceType<typeof BaseModal>>()
const textareaEl: Ref<HTMLTextAreaElement> = ref()
const textEl: Ref<HTMLInputElement> = ref()
const text: Ref<string> = ref('')

interface Props {
  title?: LangString
  titleVars?: string[]
  initial?: string
  message?: LangString
  messageVars?: string[]
  icon?: SVG_NAMES
  area?: boolean,
  dialogWidth?: string
}
const props = withDefaults(defineProps<Props>(), {
  area: false
})

const _title: Ref<LangString> = ref()
const _titleVars: Ref<string[]> = ref()
const _message: Ref<LangString> = ref()
const _messageVars: Ref<string[]> = ref()
const _initial: Ref<string> = ref()
const _icon: Ref<SVG_NAMES> = ref()
const _area: Ref<boolean[]> = ref()
const _dialogWidth: Ref<string> = ref()
const _modalProps: Ref<ModalProps> = ref()
const openAwait = async (options?: Props, modalProps?: ModalProps) => {
  setOptionsValue(_title, 'title', props, options)
  setOptionsValue(_titleVars, 'titleVars', props, options)
  setOptionsValue(_message, 'message', props, options)
  setOptionsValue(_messageVars, 'messageVars', props, options)
  setOptionsValue(_initial, 'initial', props, options)
  setOptionsValue(_icon, 'icon', props, options)
  setOptionsValue(_area, 'area', props, options)
  setOptionsValue(_dialogWidth, 'dialogWidth', props, options)
  if (_initial.value) {
    text.value = _initial.value
  } else {
    text.value = ''
  }
  _modalProps.value = modalProps
  return await modal.value.openAwait<string>(_modalProps.value)
}
defineExpose({ openAwait })

const hasHeader = computed(() => _title.value || _titleVars.value)
const isClosable = computed(() => _modalProps.value?.closeable)

const afterEnter = () => {
  if (_area.value) {
    textareaEl.value?.focus()
  } else {
    textEl.value?.focus()
  }
}

const confirm = () => {
  modal.value.resolve(text.value)
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps {
  .jps-modal-input {
    .jps-modal-content {
      padding-top: 0;
    }
    input,
    textarea {
      width: 100%;
    }
  }
}
</style>
